import { Directive, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BreadcrumbItem } from "common/breadcrumb/breadcrumb-item";
import { SlotService } from "common/slot.service";
import { MainService } from "core/main.service";
import { ViewService } from "core/view.service";
import { ViewableBreadcrumbService } from "process/breadcrumb.service";
import { MenuFactoryService } from "process/menu-factory.service";
import { BaseComponent } from "process/view/base.component";
import { ComponentService } from "process/view/component.service";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

type View = stages.process.View;
type Perspective = stages.process.Perspective;
type ProcessView = stages.process.ProcessView;

@Directive()
export class WidgetContainerComponent extends BaseComponent implements OnInit, OnDestroy {
	private route = inject(ActivatedRoute);
	private mainService = inject(MainService);
	private viewService = inject(ViewService);
	private breadcrumbService = inject(ViewableBreadcrumbService);
	private menuFactoryService = inject(MenuFactoryService);

	self?: ProcessView;
	perspectives: Perspective[] = [];
	perspective?: string;
	menuItems: MenuItem[] = [];
	breadcrumb: BreadcrumbItem[] = [];
	destroy$: Subject<boolean> = new Subject<boolean>();

	constructor() {
		const componentService = inject(ComponentService);
		const slotService = inject(SlotService);

		super(componentService, slotService);
	}

	ngOnInit(): void {
		if (this.type) {
			setTimeout(() => {
				this.mainService.applicationState.toolbarVisible = this.type!.toolbar;
			});
		}
		this.initializeFromView();
	}

	initializeFromView(): void {
		combineLatest([this.viewService.awaitViewObservable(), this.viewService.getPerspective(), this.route.paramMap])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([view, activePerspective, paramMap]) => {
				this.perspectives = view.perspectives;
				this.self = this.viewService.getSelf(view.processView);
				if (this.self?.changeMarker && this.self.changeMarker.perspectives) {
					this.perspectives.forEach((perspective) => {
						perspective.changeMarker = this.self?.changeMarker!.perspectives!.includes(perspective.ident);
					});
				}

				const menuItems = this.menuFactoryService.newMenuItems(
					this.self,
					this.perspectives,
					view.viewWorkspace,
					paramMap,
					this.route,
					activePerspective,
				);
				this.menuItems = menuItems;
				this.mainService.menuItems$.next(menuItems);

				const buttons: Button[] = this.perspectives.map((perspective) => ({
					id: perspective.ident,
					on: () => {
						this.viewService.forceViewReload(
							this.route.snapshot.paramMap.get("workspaceId")!,
							this.route.snapshot.paramMap.get("type")!,
							this.route.snapshot.paramMap.get("identity")!,
							perspective.ident,
							this.route.snapshot.paramMap.get("workspaceId")!,
							this.route.snapshot.paramMap.get("processVersion")!,
						);
					},
					active: () => {
						return perspective.ident === activePerspective;
					},
					title: "perspective." + perspective.ident,
					iconClass: "ico-" + perspective.ident,
					secondIconClass: perspective.changeMarker ? "ico-exclamation-mark change-marker" : undefined,
				}));
				this.mainService.menuButtons$.next(buttons);

				this.breadcrumb = this.breadcrumbService.newBreadcrumbItems(this.self, false, false);
			});
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.mainService.menuItems$.next([]);
		this.mainService.menuButtons$.next([]);
		this.destroy$.next(true);
		this.destroy$.complete();
	}
}
